import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import {
  getChangelogAll,
  createRelease,
  updateRelease,
  deleteRelease,
} from '../lib/api';
import {
  Plus,
  PencilSimple,
  Trash,
  FloppyDisk,
  X,
  Sparkle,
  ArrowRight,
  EyeSlash,
  Eye,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const EMPTY_ITEM = () => ({ title: '', body: '', cta: null });

const EMPTY_RELEASE = () => ({
  version: '',
  date: '',
  tag: '',
  items: [EMPTY_ITEM()],
  published: true,
});

function ItemEditor({ item, index, onChange, onRemove }) {
  const updateField = (field, value) => onChange({ ...item, [field]: value });
  const updateCta = (field, value) => {
    const cta = { ...(item.cta || { type: 'navigate', label: '', to: '' }), [field]: value };
    onChange({ ...item, cta });
  };
  const toggleCta = () => {
    onChange({
      ...item,
      cta: item.cta ? null : { label: '', type: 'navigate', to: '' },
    });
  };
  return (
    <div className="border border-border bg-muted p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
          Item #{index + 1}
        </span>
        <button
          type="button"
          onClick={onRemove}
          data-testid={`release-item-remove-${index}`}
          className="text-red-600 hover:text-red-800 text-xs font-bold uppercase tracking-wider"
        >
          <Trash size={14} className="inline mr-1" />
          Remove
        </button>
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider">Title</Label>
        <Input
          value={item.title}
          onChange={(e) => updateField('title', e.target.value)}
          data-testid={`release-item-title-${index}`}
          placeholder="e.g. Cryptographic SAML signature verification"
        />
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider">Body</Label>
        <Textarea
          value={item.body}
          onChange={(e) => updateField('body', e.target.value)}
          data-testid={`release-item-body-${index}`}
          rows={3}
          placeholder="What did you ship? Keep it short and sharp."
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id={`cta-toggle-${index}`}
          checked={!!item.cta}
          onChange={toggleCta}
          data-testid={`release-item-cta-toggle-${index}`}
        />
        <Label htmlFor={`cta-toggle-${index}`} className="text-xs font-bold uppercase tracking-wider cursor-pointer">
          Add a call-to-action button
        </Label>
      </div>

      {item.cta && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-border">
          <div>
            <Label className="text-xs">Label</Label>
            <Input
              value={item.cta.label || ''}
              onChange={(e) => updateCta('label', e.target.value)}
              data-testid={`release-item-cta-label-${index}`}
              placeholder="Try the Tour"
            />
          </div>
          <div>
            <Label className="text-xs">Type</Label>
            <Select
              value={item.cta.type || 'navigate'}
              onValueChange={(v) => updateCta('type', v)}
            >
              <SelectTrigger data-testid={`release-item-cta-type-${index}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="navigate">Navigate (internal route)</SelectItem>
                <SelectItem value="tour">Launch Guided Tour</SelectItem>
                <SelectItem value="external">External link (new tab)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">
              {item.cta.type === 'external' ? 'URL' : item.cta.type === 'tour' ? '—' : 'Route'}
            </Label>
            <Input
              value={item.cta.type === 'external' ? (item.cta.href || '') : (item.cta.to || '')}
              onChange={(e) =>
                updateCta(item.cta.type === 'external' ? 'href' : 'to', e.target.value)
              }
              data-testid={`release-item-cta-target-${index}`}
              disabled={item.cta.type === 'tour'}
              placeholder={item.cta.type === 'external' ? 'https://…' : '/security'}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ReleaseEditor({ initial, onSave, onCancel, isEdit }) {
  const [release, setRelease] = useState(initial || EMPTY_RELEASE());
  const [saving, setSaving] = useState(false);

  const setField = (field, value) => setRelease({ ...release, [field]: value });
  const setItem = (idx, updated) => {
    const items = [...release.items];
    items[idx] = updated;
    setRelease({ ...release, items });
  };
  const addItem = () => setRelease({ ...release, items: [...release.items, EMPTY_ITEM()] });
  const removeItem = (idx) =>
    setRelease({ ...release, items: release.items.filter((_, i) => i !== idx) });

  const validate = () => {
    if (!release.version.trim()) return 'Version is required';
    if (!release.date.trim()) return 'Date is required';
    if (!release.tag.trim()) return 'Tag is required';
    if (!release.items.length) return 'At least one item is required';
    for (const [i, it] of release.items.entries()) {
      if (!it.title.trim() || !it.body.trim()) return `Item #${i + 1} needs a title and body`;
      if (it.cta && !it.cta.label) return `Item #${i + 1} CTA needs a label`;
      if (it.cta?.type === 'navigate' && !it.cta.to) return `Item #${i + 1} CTA needs a route`;
      if (it.cta?.type === 'external' && !it.cta.href) return `Item #${i + 1} CTA needs a URL`;
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      // strip null CTAs so backend schema is happy
      const payload = {
        ...release,
        items: release.items.map(({ title, body, cta }) => {
          const out = { title, body };
          if (cta) out.cta = cta;
          return out;
        }),
      };
      if (isEdit) {
        await updateRelease(initial.version, payload);
        toast.success(`Release ${release.version} updated`);
      } else {
        await createRelease(payload);
        toast.success(`Release ${release.version} published`);
      }
      onSave();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="release-editor">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Version</Label>
          <Input
            value={release.version}
            onChange={(e) => setField('version', e.target.value)}
            data-testid="release-version-input"
            disabled={isEdit}
            placeholder="2026.02.14"
            className="font-mono"
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Date</Label>
          <Input
            value={release.date}
            onChange={(e) => setField('date', e.target.value)}
            data-testid="release-date-input"
            placeholder="Feb 14, 2026"
          />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider">Tag</Label>
          <Input
            value={release.tag}
            onChange={(e) => setField('tag', e.target.value)}
            data-testid="release-tag-input"
            placeholder="Security · Onboarding"
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="published-toggle"
          checked={!!release.published}
          onChange={(e) => setField('published', e.target.checked)}
          data-testid="release-published-toggle"
        />
        <Label htmlFor="published-toggle" className="text-xs font-bold uppercase tracking-wider cursor-pointer">
          Published (visible to users)
        </Label>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold uppercase tracking-wider text-foreground">
            Items ({release.items.length})
          </h3>
          <Button onClick={addItem} size="sm" variant="outline" data-testid="add-item-btn">
            <Plus size={14} className="mr-1" /> Add Item
          </Button>
        </div>
        {release.items.map((item, idx) => (
          <ItemEditor
            key={idx}
            item={item}
            index={idx}
            onChange={(v) => setItem(idx, v)}
            onRemove={() => removeItem(idx)}
          />
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-4 border-t border-border">
        <Button variant="outline" onClick={onCancel} data-testid="cancel-release-btn">
          <X size={14} className="mr-1" /> Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-release-btn"
          className="bg-brand hover:bg-[#001F8A]"
        >
          <FloppyDisk size={14} className="mr-1" /> {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Publish Release'}
        </Button>
      </div>
    </div>
  );
}

export default function PublishChangelog() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getChangelogAll();
      setReleases(data.releases || []);
    } catch (e) {
      toast.error('Failed to load releases');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (version) => {
    if (!window.confirm(`Delete release ${version}? This cannot be undone.`)) return;
    try {
      await deleteRelease(version);
      toast.success(`Release ${version} deleted`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const handleEdit = (release) => {
    setEditing(release);
    setShowEditor(true);
  };
  const handleNew = () => {
    setEditing(null);
    setShowEditor(true);
  };
  const handleSaved = () => {
    setShowEditor(false);
    setEditing(null);
    load();
  };

  return (
    <Layout>
      <div className="px-6 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Sparkle size={18} weight="fill" className="text-brand" />
              <span className="text-[10px] font-bold tracking-[0.2em] text-brand uppercase">
                Admin
              </span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight">Publish Release Notes</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Compose changelog entries and push them live to every user&rsquo;s What&rsquo;s New drawer.
            </p>
          </div>
          <Button
            onClick={handleNew}
            data-testid="new-release-btn"
            className="bg-brand hover:bg-[#001F8A] text-xs uppercase tracking-wider font-bold"
          >
            <Plus size={16} className="mr-1.5" /> New Release
          </Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm">Loading releases…</p>
        ) : releases.length === 0 ? (
          <div className="border-2 border-dashed border-border p-12 text-center">
            <p className="text-muted-foreground mb-3">No releases yet.</p>
            <Button onClick={handleNew} variant="outline">
              <Plus size={14} className="mr-1" /> Create your first release
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="release-list">
            {releases.map((r) => (
              <div
                key={r.version}
                data-testid={`release-row-${r.version}`}
                className="border border-border p-5 bg-card hover:border-brand transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-sm font-bold">v{r.version}</span>
                      <span className="text-xs text-muted-foreground">· {r.date}</span>
                      <span className="inline-block px-1.5 py-0.5 bg-muted text-[10px] font-bold tracking-wider uppercase">
                        {r.tag}
                      </span>
                      {r.published ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold tracking-wider uppercase">
                          <Eye size={10} /> Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-slate-200 text-foreground text-[10px] font-bold tracking-wider uppercase">
                          <EyeSlash size={10} /> Draft
                        </span>
                      )}
                    </div>
                    <ul className="text-sm text-muted-foreground space-y-1 mt-2">
                      {r.items.slice(0, 3).map((it, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <ArrowRight size={12} className="mt-1 text-brand shrink-0" />
                          <span>
                            <b className="text-foreground">{it.title}</b>
                            {it.cta && (
                              <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-brand">
                                · CTA: {it.cta.label}
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                      {r.items.length > 3 && (
                        <li className="text-xs text-muted-foreground ml-5">+ {r.items.length - 3} more…</li>
                      )}
                    </ul>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(r)}
                      data-testid={`edit-release-${r.version}`}
                    >
                      <PencilSimple size={14} className="mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(r.version)}
                      data-testid={`delete-release-${r.version}`}
                      className="text-red-600 hover:text-red-800"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold tracking-tight">
                {editing ? `Edit Release · v${editing.version}` : 'Publish New Release'}
              </DialogTitle>
            </DialogHeader>
            {showEditor && (
              <ReleaseEditor
                initial={editing}
                isEdit={!!editing}
                onSave={handleSaved}
                onCancel={() => setShowEditor(false)}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
